import { useEffect, useRef, useState } from 'react'
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { setUserRole, apiErrorMessage } from '@/api/client'
import { usePageMeta } from '@/hooks/use-page-meta'
import '@/styles/aura-app.css' // wspinner / wbanner / sr-only helpers
import '@/styles/aura-auth.css' // pre-app world + the split styles below

type Role = 'candidate' | 'recruiter'

const ICONS = {
  candidate: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="8" r="4" />
      <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
    </svg>
  ),
  recruiter: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="3" y="7" width="18" height="13" rx="2" />
      <path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M3 12h18" />
    </svg>
  ),
}

const CHECK = (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <polyline points="20 6 9 17 4 12" />
  </svg>
)

const ARROW = (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
)

const ROLE_COPY: Record<Role, { name: string; desc: string; features: string[] }> = {
  candidate: {
    name: 'Candidate',
    desc: 'Practice interviews with an AI interviewer. Upload your resume, optionally add a job description, and get a structured report with scores and feedback.',
    features: ['Voice practice on your resume', 'Job-description targeting', 'Structured score report'],
  },
  recruiter: {
    name: 'Recruiter',
    desc: "Create interviews with your own questions, send candidates a private link, and review the PDF report and audio recording when they're done.",
    features: ['2–5 of your own questions', 'Private candidate links', 'Report + audio recording'],
  },
}

const RULE_COPY =
  'One role per account — you can switch later while your current role has no interviews or invites on record.'
const SWITCH_RULE =
  'Your pick replaces your current role — switching is only possible while your current role has no interviews or invites on record.'

/**
 * Role picker (first login) and role switch via ``?switch=1``, as a
 * full-screen split: each role owns half the viewport, hovering claims a
 * side, clicking commits.
 *
 * New users (role ``''``) land here from the OAuth callback and from the
 * email/password sign-in. Existing users reach it via ``?switch=1``; without
 * that flag they are sent back, so the page cannot be used to bounce a
 * settled user around the app. Roles are exclusive — the pick replaces the
 * old role, and the server refuses a switch once the current side has been
 * used (invites created / interviews taken).
 */
export function RolePicker() {
  usePageMeta('Choose your role — Aura')
  const { user, setAuth } = useAuth()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const switching = params.get('switch') === '1'
  const [busy, setBusy] = useState<Role | null>(null)
  const [done, setDone] = useState<Role | null>(null)
  const [error, setError] = useState<{ role: Role; message: string } | null>(null)
  const navTimer = useRef<number | null>(null)

  useEffect(() => () => {
    if (navTimer.current !== null) window.clearTimeout(navTimer.current)
  }, [])

  // Authenticated users who already picked a role don't belong here — unless
  // they explicitly asked to switch. Redirect via <Navigate> rather than
  // navigate(): calling it during render is unsafe under StrictMode.
  if (user && user.role !== '' && !switching) {
    const returnTo = sessionStorage.getItem('aura_return_to')
    return <Navigate to={returnTo || '/'} replace />
  }

  const choose = async (role: Role) => {
    if (busy || done) return
    setBusy(role)
    setError(null)
    try {
      const data = await setUserRole(role)
      setAuth(data.token, { ...data.user, role: data.user.role as 'candidate' | 'recruiter', avatar_url: data.user.avatar_url ?? undefined })
      setBusy(null)
      setDone(role)
      const returnTo = sessionStorage.getItem('aura_return_to')
      sessionStorage.removeItem('aura_return_to')
      // Short confirmation beat so the "You're in" flash reads before the
      // jump; cleared on unmount so it can't yank the user off another page.
      navTimer.current = window.setTimeout(() => {
        navigate(returnTo || (role === 'recruiter' ? '/recruiter' : '/interview'), { replace: true })
      }, 700)
    } catch (err) {
      // A refused switch (409) carries the reason from the server.
      setError({ role, message: apiErrorMessage(err, 'Could not save your choice. Please try again.') })
      setBusy(null)
    }
  }

  const current = user?.role || null

  const half = (role: Role) => {
    const copy = ROLE_COPY[role]
    const isBusy = busy === role
    const isDone = done === role
    const isCurrent = current === role
    const descId = `cr-split-desc-${role}`
    // The copy can't live inside the button (a button is phrasing content,
    // and an aria-label there would mask it from screen readers). It sits
    // outside and is handed to the button as its description instead.
    const describedBy =
      error?.role === role
        ? `${descId} ${descId}-features ${descId}-alert`
        : `${descId} ${descId}-features`
    return (
      <div className={`cr-split-half${isDone ? ' is-done' : ''}`}>
        <button
          type="button"
          className="cr-split-cta"
          onClick={() => choose(role)}
          disabled={isBusy || isDone || isCurrent}
          aria-describedby={describedBy}
        >
          <span className="sr-only">Choose {copy.name}</span>
        </button>
        <span className="cr-split-icon" aria-hidden="true">{ICONS[role]}</span>
        <span className="cr-split-title">{copy.name}</span>
        <p className="cr-split-desc" id={descId}>{copy.desc}</p>
        <ul className="cr-split-features" id={`${descId}-features`}>
          {copy.features.map(f => (
            <li key={f}>{CHECK}{f}</li>
          ))}
        </ul>
        {error?.role === role && (
          <span className="wbanner cr-split-alert" role="alert" id={`${descId}-alert`}>{error.message}</span>
        )}
        {isBusy ? (
          <span className="cr-split-busy"><span className="wspinner" aria-hidden="true"></span>Setting up…</span>
        ) : isDone ? (
          <span className="cr-done-flash">{CHECK} You're in — routing to your dashboard</span>
        ) : isCurrent ? (
          <span className="cr-split-current">Current role</span>
        ) : (
          <span className="cr-split-go" aria-hidden="true">Choose {copy.name} {ARROW}</span>
        )}
      </div>
    )
  }

  return (
    <div className="cr-split">
      <h1 className="sr-only">How will you use Aura?</h1>
      <span className="cr-split-divider" aria-hidden="true"></span>
      <span className="cr-split-brand"><span className="mark" aria-hidden="true"></span>Aura</span>
      <span className="cr-split-account">
        <span className="cr-split-account-who">Signed in as {user?.email} · </span>
        <Link to="/">Back to Aura</Link>
      </span>
      {half('candidate')}
      {half('recruiter')}
      <p className="cr-split-rule">{switching ? SWITCH_RULE : RULE_COPY}</p>
    </div>
  )
}
